import { Box, Button, Typography } from "@mui/material";
import React, { useEffect, useMemo, useRef, useState } from "react";
import { Helmet } from "react-helmet";
import GoBack from "Components/GoBack";
import { useGetSubscriptionData } from "./RedeemOffer.hooks";
import useQueryParams from "Hooks/useQueryParams";
import { AvironLoading } from "Components/Icons/AvironLoading";
import { useHistory } from "react-router-dom";
import { CONSTANTS } from "consts";
import Swal from "sweetalert2";
import {
    handleCheckIsGoMembership,
    handleCheckIsUSDCurrency,
    handleCheckIsWarrantyMembership,
} from "Screens/Memberships/utils/Membership.utils";
import { GetAvironSubscriptionQuery, Plan, useClaimCancelOfferDowngradeToMonthlyMutation, useGetPlanbyIdQuery } from "services/webServer";
import { ENV } from "scripts/environment";
import moment from "moment";
import MembershipRedeemOfferModal, { IMembershipRedeemOfferModalRef } from "./MembershipRedeemOfferModal";
import posthog from "posthog-js";

const RedeemOfferDowngradeToMonthly = () => {
    const { id = "" } = useQueryParams();
    const history = useHistory();
    const [planData, setPlanData] = useState<Plan | undefined>(undefined);
    const membershipRedeemOfferSuccesRef =
          useRef<IMembershipRedeemOfferModalRef>(null);

    const { subscriptionData, isLoadingSubscription, error } =
        useGetSubscriptionData(id);
    const { refetch: getPlanData } = useGetPlanbyIdQuery({
        skip: true,
    });
    const [claimCancelOfferV2] = useClaimCancelOfferDowngradeToMonthlyMutation({
      onError: async (error) => {
            history.push(
                CONSTANTS.NAVIGATION_URL.MEMBERSHIP_DETAIL.replace(
                    ":id",
                    id || ""
                )
            );
      },
      onCompleted: () => {
        posthog.capture("web_annual_cancel_claim_offer", {
            offer_name: "50% off for 2 months",
            variant: "test"
        })
        membershipRedeemOfferSuccesRef.current?.setIsVisible(true);
        }
    })

    const isWarrantySubscription = useMemo(() => {
        if (!subscriptionData) return undefined;
        return handleCheckIsWarrantyMembership(
            subscriptionData?.avironSubscription?.plan?.productId || ""
        );
    }, [subscriptionData]);

    const isUSDCurrency = useMemo(() => {
        if (!subscriptionData) return undefined;
        return handleCheckIsUSDCurrency(
            subscriptionData?.avironSubscription?.plan?.currency || ""
        );
    }, [subscriptionData]);

    const isGoMembership = useMemo(() => {
        if (!subscriptionData) return undefined;
        return handleCheckIsGoMembership(
            (subscriptionData?.avironSubscription?.plan?.appIds as number[]) ||
                []
        );
    }, [subscriptionData]);

    const handleRedeemOffer = async() => {
      if(!id || typeof planData === "undefined") return;
      Swal.showLoading();

      await claimCancelOfferV2({
        variables:{
          planId: planData?.id as number,
          subscriptionId: id,
        }
      })
      
      Swal.close();
    };

    const handleRenderErrorContent = () => {
        return (
            <>
                {Swal.fire("Error", "Something went wrong", "error").then(
                    (result) => {
                        if (result.isConfirmed) {
                            history.replace(
                                CONSTANTS.NAVIGATION_URL.MEMBERSHIPS
                            );
                        }
                    }
                )}
                ;
            </>
        );
    };

    const handleFormatPlanId = () => {
        let planId;
        console.log("isUSDCurrency", isUSDCurrency);
        if (isWarrantySubscription) {
            planId = isGoMembership
                ? isUSDCurrency
                    ? ENV.MEMBERSHIP
                          .MONTHLY_FAMILY_MEMBERSHIP_GO_US_PLAN_ID_WARRANTY
                    : ENV.MEMBERSHIP
                          .MONTHLY_FAMILY_MEMBERSHIP_GO_CAD_PLAN_ID_WARRANTY
                : isUSDCurrency
                ? ENV.MEMBERSHIP
                      .MONTHLY_FAMILY_MEMBERSHIP_US_PLAN_ID_WITH_WARRANTY
                : ENV.MEMBERSHIP
                      .MONTHLY_FAMILY_MEMBERSHIP_CAD_PLAN_ID_WITH_WARRANTY;
        } else {
            planId = isGoMembership
                ? isUSDCurrency
                    ? ENV.MEMBERSHIP.MONTHLY_FAMILY_MEMBERSHIP_GO_US_PLAN_ID
                    : ENV.MEMBERSHIP.MONTHLY_FAMILY_MEMBERSHIP_GO_CAD_PLAN_ID
                : isUSDCurrency
                ? ENV.MEMBERSHIP.MONTHLY_FAMILY_MEMBERSHIP_US_PLAN_ID
                : ENV.MEMBERSHIP.MONTHLY_FAMILY_MEMBERSHIP_CAD_PLAN_ID;
        }
        console.log("planId", planId);
        return Number(planId);
    };

    useEffect(() => {
        if (
            typeof isWarrantySubscription === "undefined" ||
            typeof isUSDCurrency === "undefined" ||
            typeof isGoMembership === "undefined"
        )
            return;

        getPlanData({
            id: handleFormatPlanId(),
        }).then((res) => {
            if (res?.data?.getPlanById) {
                setPlanData(res?.data?.getPlanById as Plan);
            }
        });
    }, [isWarrantySubscription, isUSDCurrency, isGoMembership]);

    if (isLoadingSubscription || typeof planData === "undefined")
        return <AvironLoading />;

    if (Boolean(error)) {
        handleRenderErrorContent();
    }

    return (
        <Box
            sx={{
                marginTop: {
                    xs: "24px",
                    md: "0",
                },
                marginBottom: {
                    xs: "24px",
                    md: "0px",
                },
                paddingLeft: {
                    xs: "12px",
                    md: "0",
                },
                paddingRight: {
                    xs: "12px",
                    md: "0",
                },
            }}
        >
            <Helmet>
                <meta charSet="utf-8" />
                <title>Membership Review And Cancel | Aviron</title>
                <link rel="canonical" href={window.location.href} />
            </Helmet>
            <GoBack />
            <Box
                sx={{
                    padding: {
                        xs: "16px 12px",
                        sm: "40px",
                    },
                    backgroundColor: "#fff",
                    marginTop: "24px",
                    display: "flex",
                    flexDirection: "column",
                    justifyContent: "space-between",
                    height: {
                        xs: "60vh",
                        md: "auto",
                    },
                }}
            >
                <Box>
                    <Typography
                        sx={{
                            fontWeight: 700,
                            fontSize: {
                                xs: "18px",
                                md: "24px",
                            },
                            lineHeight: {
                                xs: "20px",
                                md: "25.08px",
                            },
                            letterSpacing: "0.01em",
                            marginBottom: "24px",
                            fontFamily: "GRIFTER",
                        }}
                    >
                        REDEEM YOUR OFFER
                    </Typography>
                    <Box
                        sx={{
                            backgroundColor: "#f4f4f4",
                            display: "flex",
                            alignContent: "center",
                            justifyContent: "space-between",
                            padding: {
                                xs: "12px",
                                md: "24px",
                            },
                            marginBottom: {
                                xs: "24px",
                                md: "40px",
                            },
                            flexDirection: {
                                xs: "column",
                                md: "row",
                            },
                        }}
                    >
                        <Typography
                            sx={{
                                fontWeight: 600,
                                fontFamily: "Work Sans",
                                fontSize: {
                                    xs: "16px",
                                    md: "20px",
                                },
                                lineHeight: {
                                    xs: "20px",
                                    md: "24px",
                                },

                                textTransform: "uppercase",
                                marginBottom: {
                                    xs: "12px",
                                    md: "0",
                                },
                            }}
                        >
                            {planData?.name?.toUpperCase()}
                        </Typography>
                        <Box
                            sx={{
                                display: "flex",
                                alignItems: "center",
                            }}
                        >
                            <Typography
                                sx={{
                                    fontFamily: "MonoSpec",
                                    fontWeight: 500,
                                    fontSize: {
                                        xs: "20px",
                                        md: "24px",
                                    },
                                    lineHeight: {
                                        xs: "24.98px",
                                        md: "29.98px",
                                    },
                                    color: "#808080",
                                    textDecoration: "line-through",
                                }}
                            >
                                ${planData?.amount}
                            </Typography>
                            <Typography
                                sx={{
                                    fontFamily: "MonoSpec",
                                    fontWeight: 500,
                                    fontSize: {
                                        xs: "20px",
                                        md: "24px",
                                    },
                                    lineHeight: {
                                        xs: "24.98px",
                                        md: "29.98px",
                                    },
                                    color: "#161616",
                                    marginLeft: "24px",
                                }}
                            >
                                ${planData?.amount * 0.5}
                            </Typography>
                        </Box>
                    </Box>
                    <Typography
                        sx={{
                            fontWeight: 400,
                            fontSize: {
                                xs: "16px",
                                md: "18px",
                            },
                            lineHeight: {
                                xs: "20px",
                                md: "21.11px",
                            },
                            color: "#161616",
                            marginBottom: {
                                xs: "0",
                                md: "40px",
                            },
                        }}
                    >
                        Beginning{" "}
                        {moment(
                            subscriptionData?.avironSubscription
                                ?.currentPeriodEnd || new Date()
                        ).format("MMMM Do YYYY")}
                        , you’ll be charged a promotional rate of $17 per month
                        for the first two months. After that, the regular
                        monthly membership fee will apply.
                    </Typography>
                </Box>
                <Box
                    sx={{
                        textAlign: "end",
                    }}
                >
                    <Button
                        className="btn-claim-offer"
                        variant="contained"
                        color="secondary"
                        sx={{
                            marginLeft: {
                                xs: "0",
                                md: "16px",
                            },
                            fontWeight: 500,
                            fontSize: "16px",
                            lineHeight: "19.98px",
                            letterSpacing: "0.01em",
                        }}
                        onClick={handleRedeemOffer}
                    >
                        REDEEM
                    </Button>
                </Box>
            </Box>
            <MembershipRedeemOfferModal
                ref={membershipRedeemOfferSuccesRef}
                data={subscriptionData as GetAvironSubscriptionQuery}
                isChoosingAnotherPlan={true}
            />
        </Box>
    );
};

export default RedeemOfferDowngradeToMonthly;
